// =============================================================================
// NotificationsPage — full in-app inbox
// =============================================================================
// Paginated history of the user's notifications with an unread-only filter and
// mark-read controls. Clicking a row marks it read and deep-links to the
// referenced resource when one exists.
// =============================================================================

import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, Spinner, useConfirm } from '@tkpod/ui';

import { api } from '@/lib/api';
import {
  notificationKeys,
  notificationHref,
  timeAgo,
  type InboxResponse,
  type InboxNotification,
} from '@/lib/notifications';
import { NotificationBody } from '@/components/notifications/NotificationBody';

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const inboxQuery = useQuery<InboxResponse, Error>({
    queryKey: notificationKeys.inbox(page, unreadOnly),
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (unreadOnly) params.set('unreadOnly', 'true');
      return api.get<InboxResponse>(`/api/v1/notifications?${params.toString()}`);
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation<unknown, Error, string>({
    mutationFn: (id) => api.post(`/api/v1/notifications/${id}/read`, {}),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation<unknown, Error, void>({
    mutationFn: () => api.post('/api/v1/notifications/read-all', {}),
    onSuccess: invalidate,
  });

  const removeOne = useMutation<unknown, Error, string>({
    mutationFn: (id) => api.delete(`/api/v1/notifications/${id}`),
    onSuccess: invalidate,
  });

  const clearRead = useMutation<unknown, Error, void>({
    mutationFn: () => api.post('/api/v1/notifications/clear-read', {}),
    onSuccess: invalidate,
  });

  const items = inboxQuery.data?.items ?? [];
  const meta = inboxQuery.data?.meta;
  const hasRead = items.some((n) => n.readAt);

  const openItem = (n: InboxNotification) => {
    if (!n.readAt) markRead.mutate(n.id);
    const href = notificationHref(n);
    if (href) navigate(href);
  };

  return (
    <div className="space-y-6">
      {confirmDialog}
      <Card>
        <CardHeader
          title="Notifications"
          description="Your account and order activity."
          action={
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={unreadOnly}
                  onChange={(e) => {
                    setUnreadOnly(e.target.checked);
                    setPage(1);
                  }}
                />
                Unread only
              </label>
              <Link
                to="/settings/notifications"
                className="text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                Preferences
              </Link>
              {(meta?.unread ?? 0) > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => markAllRead.mutate()}
                  loading={markAllRead.isPending}
                >
                  Mark all read
                </Button>
              )}
              {hasRead && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearRead.mutate()}
                  loading={clearRead.isPending}
                >
                  Clear read
                </Button>
              )}
            </div>
          }
        />

        <div className="mt-4">
          {inboxQuery.isLoading && !inboxQuery.data ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" label="Loading notifications..." />
            </div>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-text-tertiary">
              {unreadOnly ? 'No unread notifications.' : 'No notifications yet.'}
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {items.map((n) => {
                const href = notificationHref(n);
                return (
                  <li
                    key={n.id}
                    className={`group flex items-start gap-1 transition-colors hover:bg-surface-secondary ${
                      n.readAt ? '' : 'bg-brand-50/40'
                    }`}
                  >
                    <div className="flex min-w-0 flex-1 gap-3 px-4 py-4">
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          n.readAt ? 'bg-transparent' : 'bg-brand-500'
                        }`}
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => openItem(n)}
                          className="block w-full text-left text-sm font-medium text-text-primary hover:text-brand-700"
                        >
                          {n.title}
                        </button>
                        <NotificationBody
                          notification={n}
                          className="mt-0.5"
                          onInteract={() => {
                            if (!n.readAt) markRead.mutate(n.id);
                          }}
                        />
                        <p className="mt-1 text-xs text-text-tertiary">
                          {timeAgo(n.createdAt)}
                          {href && (
                            <button
                              type="button"
                              onClick={() => openItem(n)}
                              className="ml-1 font-medium text-brand-600 hover:text-brand-700"
                            >
                              · View
                            </button>
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Delete notification',
                          description:
                            'This notification will be permanently removed from your inbox.',
                          confirmLabel: 'Delete',
                          tone: 'danger',
                        });
                        if (ok) removeOne.mutate(n.id);
                      }}
                      disabled={removeOne.isPending}
                      aria-label="Delete notification"
                      title="Delete"
                      className="mr-2 mt-3 shrink-0 rounded-md p-1.5 text-text-tertiary opacity-0 transition-opacity hover:bg-surface-tertiary hover:text-danger focus:opacity-100 group-hover:opacity-100"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="h-4 w-4"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm text-text-secondary">
            <span>
              Page {meta.page} of {meta.totalPages} · {meta.total} total
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={meta.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
